import React, { useState, useEffect, useContext, useRef } from 'react';
import { Button } from '@mui/material';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useLocation } from 'react-router-dom';
import { useEmailConfig } from './EmailConfigContext';
import { UserContext } from '../../../../../context/UserContext';
import './params.css';

function EmailParameters() {
    // Default parameters
    const defaultParams = {
        userEmail: "",
        dynamicFolders: {},
        keywords: {
            decline: ['seo', 'partnership', 'opportunity'],
            forward: ['invoice', 'attached', 'consultation']
        },
        exclusions: {
            list: ['Calendar (.ics)']
        },
        tones: {
            decline: 3,
            forward: 3
        },
        aiFeatures: {
            classification: true,
            replyGeneration: true,
            replyDetection: true
        }
    };

    const location = useLocation();
    const { parameters, updateParameters } = useEmailConfig();
    const { user } = useContext(UserContext);
    const [statusBar, setStatusBar] = useState({ show: false, message: '', isSuccess: true });
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading parameters...');
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState('error'); // 'error' or 'neutral'
    const [userEmails, setUserEmails] = useState(() => {
        // Initialize from localStorage if available
        const saved = localStorage.getItem('emailAutomation_userEmails');

        if (!saved) {
            return [];
        }

        try {
            const parsedEmails = JSON.parse(saved);
            return Array.isArray(parsedEmails)
                ? parsedEmails.map(email => (typeof email === 'string' ? email.trim() : '')).filter(Boolean)
                : [];
        } catch (storageError) {
            console.error('Error reading stored email configuration:', storageError);
            return [];
        }
    });
    const [selectedEmail, setSelectedEmail] = useState(() => {
        // Initialize from localStorage if available
        return (localStorage.getItem('emailAutomation_selectedEmail') || '').trim();
    });

    // Dynamic folders state - initialize from localStorage if available
    // Structure: { id: { name: string, keywords: {...} } }
    const [dynamicFolders, setDynamicFolders] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_dynamicFolders');
        if (saved) {
            const parsed = JSON.parse(saved);
            // Convert old format (name as key) to new format (id as key with name property)
            const converted = {};
            Object.entries(parsed).forEach(([key, value], index) => {
                // If it already has an id and name property, it's in new format
                if (value.id && value.name !== undefined) {
                    converted[value.id] = value;
                } else {
                    // Old format: key is the name
                    const id = `folder_${Date.now()}_${index}`;
                    converted[id] = {
                        id,
                        name: key,
                        keywords: value.keywords || { english: [] }
                    };
                }
            });
            return converted;
        }
        return {};
    });

    // Other keywords state - initialize from localStorage if available
    const [declineKeywords, setDeclineKeywords] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_declineKeywords');
        return saved ? JSON.parse(saved) : [];
    });
    const [forwardKeywords, setForwardKeywords] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_forwardKeywords');
        return saved ? JSON.parse(saved) : [];
    });
    const [excludeKeywords, setExcludeKeywords] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_excludeKeywords');
        return saved ? JSON.parse(saved) : [];
    });
    const [declineTone, setDeclineTone] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_declineTone');
        return saved ? parseInt(saved) : 3;
    });
    const [forwardTone, setForwardTone] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_forwardTone');
        return saved ? parseInt(saved) : 3;
    });
    const [aiClassification, setAiClassification] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_aiClassification');
        return saved ? JSON.parse(saved) : true;
    });
    const [aiReplyGeneration, setAiReplyGeneration] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_aiReplyGeneration');
        return saved ? JSON.parse(saved) : true;
    });
    const [aiReplyDetection, setAiReplyDetection] = useState(() => {
        const saved = localStorage.getItem('emailAutomation_aiReplyDetection');
        return saved ? JSON.parse(saved) : true;
    });
    const [conflictingFields, setConflictingFields] = useState([]);
    const [expandedFolders, setExpandedFolders] = useState({}); // Track which folders are expanded
    const [expandedIntentCategories, setExpandedIntentCategories] = useState({
        decline: true,
        forward: true,
        exclude: true
    }); // Track which intent detection categories are expanded
    const [isDynamicFoldersExpanded, setIsDynamicFoldersExpanded] = useState(true); // Track if Dynamic Folders section is expanded
    const [isIntentDetectionExpanded, setIsIntentDetectionExpanded] = useState(true); // Track if Intent Detection section is expanded
    const [isDraftResponseToneExpanded, setIsDraftResponseToneExpanded] = useState(true); // Track if Draft Response Tone section is expanded
    const [isDynamicFoldersHelpOpen, setIsDynamicFoldersHelpOpen] = useState(false);
    const [isIntentDetectionHelpOpen, setIsIntentDetectionHelpOpen] = useState(false);
    const [isDraftResponseToneHelpOpen, setIsDraftResponseToneHelpOpen] = useState(false);
    const [isAiFeaturesHelpOpen, setIsAiFeaturesHelpOpen] = useState(false);
    const [hideFolderKeywords, setHideFolderKeywords] = useState(true); // Hide folder keywords for now
    const [folderToDelete, setFolderToDelete] = useState(null);
    const [highlightedDynamicFoldersControl, setHighlightedDynamicFoldersControl] = useState('');
    const [highlightedIntentDetectionControl, setHighlightedIntentDetectionControl] = useState('');
    const [highlightedDraftResponseToneControl, setHighlightedDraftResponseToneControl] = useState('');
    const [highlightedAiFeaturesControl, setHighlightedAiFeaturesControl] = useState('');

    const startLoading = (message) => {
        setLoadingMessage(message);
        setLoading(true);
    };

    const stopLoading = () => {
        setLoading(false);
        setLoadingMessage('Loading parameters...');
    };

    // State for keyword editing
    const [editingKeyword, setEditingKeyword] = useState({ folderId: null, index: null, value: '', lang: null, category: null });
    const [newKeywordsInput, setNewKeywordsInput] = useState({}); // Per-folder/category input for batch adding
    const [keywordToDelete, setKeywordToDelete] = useState(null); // For delete confirmation

    // Flag to prevent useEffect from overwriting data during API updates
    const [isUpdatingFromApi, setIsUpdatingFromApi] = useState(false);

    const [newEmailInput, setNewEmailInput] = useState('');
    const [emailToDelete, setEmailToDelete] = useState(null);
    const emailInputRef = useRef(null);
    const addFolderButtonRef = useRef(null);
    const firstRemoveFolderButtonRef = useRef(null);
    const firstFolderNameInputRef = useRef(null);
    const firstDescriptionTextareaRef = useRef(null);
    const guideHighlightTimeoutRef = useRef(null);
    const declineLabelRef = useRef(null);
    const forwardLabelRef = useRef(null);
    const excludeLabelRef = useRef(null);
    const declineInputRef = useRef(null);
    const declineAddButtonRef = useRef(null);
    const declineDeleteButtonRef = useRef(null);
    const forwardDeleteButtonRef = useRef(null);
    const excludeDeleteButtonRef = useRef(null);
    const intentGuideHighlightTimeoutRef = useRef(null);
    const declineToneGroupRef = useRef(null);
    const forwardToneGroupRef = useRef(null);
    const declineToneScaleRef = useRef(null);
    const draftToneGuideHighlightTimeoutRef = useRef(null);
    const aiClassificationItemRef = useRef(null);
    const aiReplyGenerationItemRef = useRef(null);
    const aiFeaturesGuideHighlightTimeoutRef = useRef(null);
    const configuredEmails = userEmails
        .map(email => (typeof email === 'string' ? email.trim() : ''))
        .filter(Boolean);
    const configuredEmailsValue = configuredEmails.join(',');
    const hasConfiguredEmails = configuredEmails.length > 0;
    const needsEmailSetup = !hasConfiguredEmails;

    useEffect(() => {
        if (!needsEmailSetup) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            if (emailInputRef.current) {
                emailInputRef.current.focus();
                emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [needsEmailSetup]);

    useEffect(() => {
        return () => {
            if (guideHighlightTimeoutRef.current) {
                window.clearTimeout(guideHighlightTimeoutRef.current);
            }
            if (intentGuideHighlightTimeoutRef.current) {
                window.clearTimeout(intentGuideHighlightTimeoutRef.current);
            }
            if (draftToneGuideHighlightTimeoutRef.current) {
                window.clearTimeout(draftToneGuideHighlightTimeoutRef.current);
            }
            if (aiFeaturesGuideHighlightTimeoutRef.current) {
                window.clearTimeout(aiFeaturesGuideHighlightTimeoutRef.current);
            }
        };
    }, []);

    // useEffect for initializing state and fetching data when needed
    useEffect(() => {
        // Initialize from context on first load if we have parameters data
        // This ensures we preserve data when navigating between tabs
        // Only update if we don't already have data loaded (prevents overwriting API-fetched data)
        const hasExistingFolderData = Object.keys(dynamicFolders).length > 0;
        const hasExistingKeywordData = declineKeywords.length > 0 || forwardKeywords.length > 0;
        const hasExistingEmails = configuredEmails.length > 0;

        if ((parameters.dynamicFolders || parameters.keywords || parameters.userEmail) && !hasExistingFolderData && !hasExistingKeywordData && !hasExistingEmails) {
            // Initialize userEmails from context parameters
            // Split the userEmail string into an array if it exists
            if (parameters.userEmail && parameters.userEmail.trim() !== '') {
                const emailArray = parameters.userEmail.split(',').map(email => email.trim());
                setUserEmails(emailArray);
            }

            // Initialize dynamic folders
            if (parameters.dynamicFolders) {
                setDynamicFolders(parameters.dynamicFolders);
            } else if (parameters.folders) {
                // Backward compatibility with old structure
                const convertedFolders = {};
                if (parameters.folders.urgent) {
                    convertedFolders[parameters.folders.urgent] = {
                        keywords: {
                            english: Array.isArray(parameters.keywords?.urgent)
                                ? parameters.keywords.urgent
                                : Array.isArray(parameters.keywords?.urgent?.english)
                                    ? parameters.keywords.urgent.english
                                    : Array.isArray(parameters.urgentKeywords)
                                        ? parameters.urgentKeywords
                                        : []
                        }
                    };
                }
                if (parameters.folders.review) {
                    convertedFolders[parameters.folders.review] = {
                        keywords: {
                            english: Array.isArray(parameters.keywords?.review)
                                ? parameters.keywords.review
                                : Array.isArray(parameters.keywords?.review?.english)
                                    ? parameters.keywords.review.english
                                    : Array.isArray(parameters.reviewKeywords)
                                        ? parameters.reviewKeywords
                                        : []
                        }
                    };
                }
                if (parameters.folders.important) {
                    convertedFolders[parameters.folders.important] = {
                        keywords: {
                            english: Array.isArray(parameters.keywords?.important)
                                ? parameters.keywords.important
                                : Array.isArray(parameters.keywords?.important?.english)
                                    ? parameters.keywords.important.english
                                    : Array.isArray(parameters.importantKeywords)
                                        ? parameters.importantKeywords
                                        : []
                        }
                    };
                }
                setDynamicFolders(convertedFolders);
            } else {
                // Default folders
                setDynamicFolders(defaultParams.dynamicFolders);
            }

            // Set other keyword arrays
            // Only set keywords if they exist in the parameters, otherwise start with empty arrays
            setDeclineKeywords(
                Array.isArray(parameters.keywords?.decline)
                    ? parameters.keywords.decline
                    : Array.isArray(parameters.keywords?.decline?.english)
                        ? parameters.keywords.decline.english
                        : Array.isArray(parameters.declineKeywords)
                            ? parameters.declineKeywords
                            : ['']
            );
            setForwardKeywords(
                Array.isArray(parameters.keywords?.forward)
                    ? parameters.keywords.forward
                    : Array.isArray(parameters.keywords?.forward?.english)
                        ? parameters.keywords.forward.english
                        : Array.isArray(parameters.forwardKeywords)
                            ? parameters.forwardKeywords
                            : ['']
            );
            setExcludeKeywords(
                Array.isArray(parameters.exclusions?.list)
                    ? parameters.exclusions.list
                    : Array.isArray(parameters.excludeKeywords)
                        ? parameters.excludeKeywords
                        : ['']
            );

            setDeclineTone(parameters.tones?.decline || parameters.declineTone || defaultParams.tones.decline);
            setForwardTone(parameters.tones?.forward || parameters.forwardTone || defaultParams.tones.forward);
            setAiClassification(parameters.aiFeatures?.classification ?? parameters.useAI?.classification ?? defaultParams.aiFeatures.classification);
            setAiReplyGeneration(parameters.aiFeatures?.replyGeneration ?? parameters.useAI?.replyGeneration ?? defaultParams.aiFeatures.replyGeneration);
            setAiReplyDetection(parameters.aiFeatures?.replyDetection ?? parameters.useAI?.replyDetection ?? defaultParams.aiFeatures.replyDetection);
        }
    }, [parameters]); // Depend on parameters

    // Sync userEmails to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('emailAutomation_userEmails', JSON.stringify(configuredEmails));
    }, [configuredEmailsValue]);

    // Sync userEmails to context so other tabs (Dashboard, Schedule) can see them
    useEffect(() => {
        // Only update if we have emails and the context doesn't match
        const currentContextEmails = parameters.userEmail ? parameters.userEmail.split(',').map(e => e.trim()).filter(e => e) : [];
        const hasMeaningfulChange = configuredEmails.length !== currentContextEmails.length ||
            configuredEmails.some((email, idx) => email !== currentContextEmails[idx]);

        if (hasMeaningfulChange) {
            updateParameters({
                ...parameters,
                userEmail: configuredEmailsValue
            });
        }
    }, [configuredEmailsValue, parameters.userEmail]);

    // Sync selectedEmail to localStorage whenever it changes
    useEffect(() => {
        if (selectedEmail) {
            localStorage.setItem('emailAutomation_selectedEmail', selectedEmail);
            return;
        }

        localStorage.removeItem('emailAutomation_selectedEmail');
    }, [selectedEmail]);

    useEffect(() => {
        if (!hasConfiguredEmails) {
            if (selectedEmail) {
                setSelectedEmail('');
            }
            return;
        }

        if (!configuredEmails.includes(selectedEmail)) {
            setSelectedEmail(configuredEmails[0]);
        }
    }, [configuredEmailsValue, hasConfiguredEmails, selectedEmail]);

    // Sync all parameter states to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('emailAutomation_dynamicFolders', JSON.stringify(dynamicFolders));
    }, [dynamicFolders]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_declineKeywords', JSON.stringify(declineKeywords));
    }, [declineKeywords]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_forwardKeywords', JSON.stringify(forwardKeywords));
    }, [forwardKeywords]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_excludeKeywords', JSON.stringify(excludeKeywords));
    }, [excludeKeywords]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_declineTone', declineTone.toString());
    }, [declineTone]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_forwardTone', forwardTone.toString());
    }, [forwardTone]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_aiClassification', JSON.stringify(aiClassification));
    }, [aiClassification]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_aiReplyGeneration', JSON.stringify(aiReplyGeneration));
    }, [aiReplyGeneration]);

    useEffect(() => {
        localStorage.setItem('emailAutomation_aiReplyDetection', JSON.stringify(aiReplyDetection));
    }, [aiReplyDetection]);

    // Fetch parameters data from the API
    const fetchParametersData = async (emailToFetch = null) => {
        // If a specific email is provided, use it; otherwise count valid emails in the form
        let emailToUse;

        if (emailToFetch) {
            emailToUse = emailToFetch.trim();
        } else {
            // Count valid emails in the form
            const validEmails = userEmails.filter(email => email && email.trim() !== '');

            // If there are no emails entered, show a message and return
            if (validEmails.length === 0) {
                showStatus('Enter an email address to retrieve existing parameters.', false, true);
                return;
            }

            // If there are multiple emails, show a message and update context with email list
            if (validEmails.length > 1) {
                showStatus('Cannot retrieve parameters for multiple email addresses. You can still save parameters for multiple emails.', false, true);

                // Even though we can't fetch parameters, we should update the context with the email list
                // so it persists when navigating between views
                const paramsToUpdate = {
                    userEmail: userEmails.filter(email => email.trim() !== '').join(',')
                };

                // Set flag to prevent useEffect from reinitializing
                setIsUpdatingFromApi(true);
                // Update the parameters in the context with just the email list
                updateParameters(paramsToUpdate);

                // Reset flag after a short delay to allow useEffect to run normally again
                setTimeout(() => {
                    setIsUpdatingFromApi(false);
                }, 0);

                return;
            }

            // Use the single email from the form
            emailToUse = validEmails[0].trim();
        }

        startLoading('Loading parameters...');
        setError(null);

        try {
            // Prepare headers with authorization
            const headers = {
                'Content-Type': 'application/json',
            };

            // Add authorization header if user is available
            if (user && user.tokenType && user.accessToken) {
                headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
            }

            const response = await fetch(`/server/rest/ai-agent/parameters/${emailToUse}`, {
                method: 'GET',
                headers: headers,
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Parse the parameters data from the response
                let paramsData;
                try {
                    paramsData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
                } catch (parseError) {
                    console.error('Error parsing parameters data:', parseError);
                    setError('Failed to parse parameters data');
                    return;
                }

                // Note: We don't call updateParameters here to avoid triggering the useEffect
                // that would overwrite our freshly loaded data. The local state is set directly below.

                // Update local state with the fetched parameters data
                // Keep the existing userEmails instead of replacing them with data from API
                // (API response doesn't contain userEmail field, so this was clearing the input)
                    if (paramsData.dynamicFolders) {
                        // Convert old format (name as key) to new ID-based format
                        const convertedFolders = {};
                        Object.entries(paramsData.dynamicFolders).forEach(([folderName, folderData], index) => {
                            const id = `folder_${Date.now()}_${index}`;
                            convertedFolders[id] = {
                                id,
                                name: folderName,
                                description: folderData.description || '',
                                keywords: folderData.keywords || { english: [] }
                            };
                        });
                        setDynamicFolders(convertedFolders);
                } else if (paramsData.folders) {
                    // Backward compatibility with old structure
                    const convertedFolders = {};
                    if (paramsData.folders.urgent) {
                        convertedFolders[paramsData.folders.urgent] = {
                            keywords: {
                                english: Array.isArray(paramsData.keywords?.urgent)
                                    ? paramsData.keywords.urgent
                                    : Array.isArray(paramsData.keywords?.urgent?.english)
                                        ? paramsData.keywords.urgent.english
                                        : ['']
                            }
                        };
                    }
                    if (paramsData.folders.review) {
                        convertedFolders[paramsData.folders.review] = {
                            keywords: {
                                english: Array.isArray(paramsData.keywords?.review)
                                    ? paramsData.keywords.review
                                    : Array.isArray(paramsData.keywords?.review?.english)
                                        ? paramsData.keywords.review.english
                                        : ['']
                            }
                        };
                    }
                    if (paramsData.folders.important) {
                        convertedFolders[paramsData.folders.important] = {
                            keywords: {
                                english: Array.isArray(paramsData.keywords?.important)
                                    ? paramsData.keywords.important
                                    : Array.isArray(paramsData.keywords?.important?.english)
                                        ? paramsData.keywords.important.english
                                        : ['']
                            }
                        };
                    }
                    setDynamicFolders(convertedFolders);
                }

                // Set other keyword arrays
                setDeclineKeywords(
                    Array.isArray(paramsData.keywords?.decline)
                        ? paramsData.keywords.decline
                        : Array.isArray(paramsData.keywords?.decline?.english)
                            ? paramsData.keywords.decline.english
                            : ['']
                );
                setForwardKeywords(
                    Array.isArray(paramsData.keywords?.forward)
                        ? paramsData.keywords.forward
                        : Array.isArray(paramsData.keywords?.forward?.english)
                            ? paramsData.keywords.forward.english
                            : ['']
                );
                setExcludeKeywords(
                    Array.isArray(paramsData.exclusions?.list)
                        ? paramsData.exclusions.list
                        : ['']
                );

                setDeclineTone(paramsData.tones?.decline || 3);
                setForwardTone(paramsData.tones?.forward || 3);
                setAiClassification(paramsData.aiFeatures?.classification ?? true);
                setAiReplyGeneration(paramsData.aiFeatures?.replyGeneration ?? true);
                setAiReplyDetection(paramsData.aiFeatures?.replyDetection ?? true);

                // Set the selected email to track which email's config is currently displayed
                setSelectedEmail(emailToUse);

                // Show success message
                showStatus("Parameters loaded successfully from the server!");
            } else {
                // Check if this is a "not found" error
                if (data.message && data.message.includes('Error retrieving parameters')) {
                    // Show a neutral message instead of an error for "not found" cases
                    showStatus('No configuration found for this user. You can create one by saving parameters.', false, true);
                    // Clear any existing parameters in the context
                    setIsUpdatingFromApi(true);
                    updateParameters({
                        userEmail: emailToUse,
                        dynamicFolders: {},
                        keywords: {
                            decline: [],
                            forward: []
                        },
                        exclusions: {
                            list: []
                        },
                        tones: {
                            decline: 3,
                            forward: 3
                        },
                        aiFeatures: {
                            classification: true,
                            replyGeneration: true,
                            replyDetection: true
                        }
                    });

                    // Reset flag after a short delay to allow useEffect to run normally again
                    setTimeout(() => {
                        setIsUpdatingFromApi(false);
                    }, 0);
                } else {
                    setError(data.message || 'Failed to fetch parameters data');
                }
            }
        } catch (err) {
            setError(err.message || 'An error occurred while fetching parameters data');
        } finally {
            stopLoading();
        }
    };

    // Add a new keyword field for a folder
    const addFolderKeywordField = (folderId, index) => {
        setDynamicFolders(prev => {
            const newFolders = {...prev};
            if (!newFolders[folderId]) {
                newFolders[folderId] = { id: folderId, name: 'New Folder', keywords: { english: [] } };
            }
            if (!newFolders[folderId].keywords) {
                newFolders[folderId].keywords = { english: [] };
            }
            if (!newFolders[folderId].keywords.english) {
                newFolders[folderId].keywords.english = [];
            }
            const newKeywords = [...newFolders[folderId].keywords.english];
            newKeywords[index] = '';
            newFolders[folderId].keywords.english = newKeywords;
            return newFolders;
        });
    };

    // Remove a keyword field for a folder
    const removeFolderKeywordField = (folderId, index) => {
        setDynamicFolders(prev => {
            const newFolders = {...prev};
            if (newFolders[folderId] && newFolders[folderId].keywords && newFolders[folderId].keywords.english) {
                const newKeywords = [...newFolders[folderId].keywords.english];
                newKeywords.splice(index, 1);
                newFolders[folderId].keywords.english = newKeywords;
            }
            return newFolders;
        });
    };

    // Handle keyword change for a folder
    const handleFolderKeywordChange = (folderId, index, value) => {
        setDynamicFolders(prev => {
            const newFolders = {...prev};
            if (!newFolders[folderId]) {
                newFolders[folderId] = { id: folderId, name: 'New Folder', keywords: { english: [] } };
            }
            if (!newFolders[folderId].keywords) {
                newFolders[folderId].keywords = { english: [] };
            }
            if (!newFolders[folderId].keywords.english) {
                newFolders[folderId].keywords.english = [];
            }
            const newKeywords = [...newFolders[folderId].keywords.english];
            newKeywords[index] = value;
            newFolders[folderId].keywords.english = newKeywords;
            return newFolders;
        });
    };

    const handleSelectedEmailChange = (event) => {
        const nextSelectedEmail = event.target.value;
        setSelectedEmail(nextSelectedEmail);
        fetchParametersData(nextSelectedEmail);
    };

    const confirmDeleteSelectedEmail = () => {
        if (!selectedEmail) {
            return;
        }

        const selectedIndex = userEmails.findIndex(email => (typeof email === 'string' ? email.trim() : '') === selectedEmail);
        if (selectedIndex !== -1) {
            confirmDeleteEmail(selectedIndex);
        }
    };

    // Show delete confirmation for email
    const confirmDeleteEmail = (index) => {
        const email = userEmails[index] || '';
        setEmailToDelete({ index, email });
    };

    // Execute delete email after confirmation
    const executeDeleteEmail = () => {
        if (emailToDelete) {
            const deletedIndex = emailToDelete.index;
            const newEmails = [...userEmails];
            newEmails.splice(deletedIndex, 1);
            const remainingEmails = newEmails
                .map(email => (typeof email === 'string' ? email.trim() : ''))
                .filter(Boolean);

            setUserEmails(remainingEmails);

            // If this was the last email, reset Dynamic Folders and Intent Detection to defaults
            if (remainingEmails.length === 0) {
                // Set flag to prevent useEffect from reinitializing from old context data
                setIsUpdatingFromApi(true);
                setSelectedEmail('');

                setDynamicFolders(defaultParams.dynamicFolders);
                setDeclineKeywords([...defaultParams.keywords.decline]);
                setForwardKeywords([...defaultParams.keywords.forward]);
                setExcludeKeywords([...defaultParams.exclusions.list]);
                setDeclineTone(defaultParams.tones.decline);
                setForwardTone(defaultParams.tones.forward);
                setAiClassification(defaultParams.aiFeatures.classification);
                setAiReplyGeneration(defaultParams.aiFeatures.replyGeneration);
                setAiReplyDetection(defaultParams.aiFeatures.replyDetection);

                // Update context with default parameters
                updateParameters({
                    userEmail: '',
                    dynamicFolders: defaultParams.dynamicFolders,
                    keywords: {
                        decline: [...defaultParams.keywords.decline],
                        forward: [...defaultParams.keywords.forward]
                    },
                    exclusions: {
                        list: [...defaultParams.exclusions.list]
                    },
                    tones: { ...defaultParams.tones },
                    aiFeatures: { ...defaultParams.aiFeatures }
                });

                // Reset flag after a short delay
                setTimeout(() => {
                    setIsUpdatingFromApi(false);
                }, 0);

                showStatus('Last email removed. All settings have been reset to defaults.');
            } else {
                const targetIndex = Math.min(deletedIndex, remainingEmails.length - 1);
                const targetEmail = remainingEmails[targetIndex];
                setSelectedEmail(targetEmail);
                fetchParametersData(targetEmail);
                showStatus(`Switched to ${targetEmail}'s configuration`);
            }
        }
        setEmailToDelete(null);
    };

    // Cancel delete email
    const cancelDeleteEmail = () => {
        setEmailToDelete(null);
    };

    // Handle new email input change
    const handleNewEmailInputChange = (value) => {
        setNewEmailInput(value);
    };

    // Add new email
    const addNewEmail = () => {
        const email = newEmailInput.trim();
        if (!email) {
            showStatus('Please enter an email address', false);
            return;
        }
        if (!email.includes('@')) {
            showStatus('Please enter a valid email address', false);
            return;
        }
        if (configuredEmails.includes(email)) {
            showStatus('This email is already added', false);
            return;
        }
        setUserEmails([...configuredEmails, email]);
        setSelectedEmail(email);
        setNewEmailInput('');
        showStatus('Email added successfully', true);
        // Fetch parameters from API for the new email
        fetchParametersData(email);
    };

    // Handle key press in new email input
    const handleNewEmailKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addNewEmail();
        }
    };

    // Add a new folder
    const addFolder = () => {
        const newId = `folder_${Date.now()}`;
        const newFolderName = `New Folder ${Object.keys(dynamicFolders).length + 1}`;
        setDynamicFolders(prev => {
            // Create new folder entry with ID-based structure
            const newFolderEntry = {
                [newId]: {
                    id: newId,
                    name: newFolderName,
                    keywords: {
                        english: ['']
                    }
                }
            };
            // Add new folder at the beginning, then spread existing folders
            return {
                ...newFolderEntry,
                ...prev
            };
        });
    };

    // Remove a folder
    const removeFolder = (folderId) => {
        const folderName = dynamicFolders[folderId]?.name || 'Untitled Folder';
        setFolderToDelete({ id: folderId, name: folderName });
    };

    const executeRemoveFolder = () => {
        if (!folderToDelete) {
            return;
        }

        const removedFolderName = folderToDelete.name;

        setDynamicFolders(prev => {
            const newFolders = { ...prev };
            delete newFolders[folderToDelete.id];
            return newFolders;
        });

        setFolderToDelete(null);
        setHighlightedDynamicFoldersControl('');
        showStatus(`Folder "${removedFolderName}" removed.`, true);
    };

    const cancelRemoveFolder = () => {
        setFolderToDelete(null);
    };

    // Update folder name
    const updateFolderName = (folderId, newName) => {
        if (!newName.trim()) {
            showStatus("Folder name cannot be empty", false);
            return;
        }

        // Check if another folder already has this name
        const existingFolder = Object.values(dynamicFolders).find(
            folder => folder.name === newName && folder.id !== folderId
        );
        if (existingFolder) {
            showStatus("A folder with this name already exists", false);
            return;
        }

        setDynamicFolders(prev => {
            const newFolders = {...prev};
            // Update just the name property, keeping the same ID
            if (newFolders[folderId]) {
                newFolders[folderId] = {
                    ...newFolders[folderId],
                    name: newName
                };
            }
            return newFolders;
        });
    };

    // Update folder description
    const updateFolderDescription = (folderId, newDescription) => {
        setDynamicFolders(prev => {
            const newFolders = {...prev};
            if (newFolders[folderId]) {
                newFolders[folderId] = {
                    ...newFolders[folderId],
                    description: newDescription
                };
            }
            return newFolders;
        });
    };

    // Toggle folder expand/collapse
    const toggleFolderExpand = (folderName) => {
        setExpandedFolders(prev => ({
            ...prev,
            [folderName]: !prev[folderName]
        }));
    };

    // Check if folder is expanded (default to false - collapsed)
    const isFolderExpanded = (folderName) => {
        return expandedFolders[folderName] === true;
    };

    const getFirstDynamicFolderId = () => Object.keys(dynamicFolders)[0] || '';

    const clearDynamicFoldersGuideHighlight = () => {
        if (guideHighlightTimeoutRef.current) {
            window.clearTimeout(guideHighlightTimeoutRef.current);
            guideHighlightTimeoutRef.current = null;
        }

        setHighlightedDynamicFoldersControl('');
    };

    const highlightDynamicFoldersControl = (controlKey, element, shouldFocus = true) => {
        if (!element) {
            return;
        }

        clearDynamicFoldersGuideHighlight();
        setHighlightedDynamicFoldersControl(controlKey);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (shouldFocus && typeof element.focus === 'function') {
            element.focus();
        }

        guideHighlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightedDynamicFoldersControl('');
            guideHighlightTimeoutRef.current = null;
        }, 2400);
    };

    const handleDynamicFoldersGuideAction = (controlKey) => {
        setIsDynamicFoldersExpanded(true);

        const firstDynamicFolderId = getFirstDynamicFolderId();

        if (!firstDynamicFolderId && controlKey !== 'add-folder') {
            showStatus('Add a folder first to use that action.', false, true);
            window.setTimeout(() => {
                highlightDynamicFoldersControl('add-folder', addFolderButtonRef.current);
            }, 120);
            return;
        }

        switch (controlKey) {
            case 'add-folder':
                window.setTimeout(() => {
                    highlightDynamicFoldersControl('add-folder', addFolderButtonRef.current);
                }, 120);
                break;
            case 'remove-folder':
                window.setTimeout(() => {
                    highlightDynamicFoldersControl('remove-folder', firstRemoveFolderButtonRef.current);
                }, 120);
                break;
            case 'folder-name':
                window.setTimeout(() => {
                    highlightDynamicFoldersControl('folder-name', firstFolderNameInputRef.current);
                }, 120);
                break;
            case 'description':
                if (!isFolderExpanded(firstDynamicFolderId)) {
                    setExpandedFolders(prev => ({
                        ...prev,
                        [firstDynamicFolderId]: true
                    }));
                }

                window.setTimeout(() => {
                    highlightDynamicFoldersControl('description', firstDescriptionTextareaRef.current);
                }, isFolderExpanded(firstDynamicFolderId) ? 120 : 220);
                break;
            default:
                break;
        }
    };

    // Toggle intent category expand/collapse
    const toggleIntentCategory = (category) => {
        setExpandedIntentCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    // Check if intent category is expanded
    const isIntentCategoryExpanded = (category) => {
        return expandedIntentCategories[category] !== false; // Default to true (expanded)
    };

    const clearIntentDetectionGuideHighlight = () => {
        if (intentGuideHighlightTimeoutRef.current) {
            window.clearTimeout(intentGuideHighlightTimeoutRef.current);
            intentGuideHighlightTimeoutRef.current = null;
        }

        setHighlightedIntentDetectionControl('');
    };

    const highlightIntentDetectionControl = (controlKey, element, shouldFocus = false) => {
        if (!element) {
            return;
        }

        clearIntentDetectionGuideHighlight();
        setHighlightedIntentDetectionControl(controlKey);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (shouldFocus && typeof element.focus === 'function') {
            element.focus();
        }

        intentGuideHighlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightedIntentDetectionControl('');
            intentGuideHighlightTimeoutRef.current = null;
        }, 2400);
    };

    const getIntentDeleteTarget = () => {
        if (declineKeywords.length > 0) {
            return { category: 'decline', element: declineDeleteButtonRef.current };
        }
        if (forwardKeywords.length > 0) {
            return { category: 'forward', element: forwardDeleteButtonRef.current };
        }
        if (excludeKeywords.length > 0) {
            return { category: 'exclude', element: excludeDeleteButtonRef.current };
        }

        return { category: '', element: null };
    };

    const handleIntentDetectionGuideAction = (controlKey) => {
        setIsIntentDetectionExpanded(true);

        switch (controlKey) {
            case 'decline-label':
                window.setTimeout(() => {
                    highlightIntentDetectionControl('decline-label', declineLabelRef.current);
                }, 120);
                break;
            case 'forward-label':
                window.setTimeout(() => {
                    highlightIntentDetectionControl('forward-label', forwardLabelRef.current);
                }, 120);
                break;
            case 'exclude-label':
                window.setTimeout(() => {
                    highlightIntentDetectionControl('exclude-label', excludeLabelRef.current);
                }, 120);
                break;
            case 'input': {
                const wasExpanded = isIntentCategoryExpanded('decline');
                setExpandedIntentCategories(prev => ({ ...prev, decline: true }));
                window.setTimeout(() => {
                    highlightIntentDetectionControl('input', declineInputRef.current, true);
                }, wasExpanded ? 120 : 220);
                break;
            }
            case 'add-button': {
                const wasExpanded = isIntentCategoryExpanded('decline');
                setExpandedIntentCategories(prev => ({ ...prev, decline: true }));
                window.setTimeout(() => {
                    highlightIntentDetectionControl('add-button', declineAddButtonRef.current, true);
                }, wasExpanded ? 120 : 220);
                break;
            }
            case 'delete-button': {
                const target = getIntentDeleteTarget();

                if (!target.category || !target.element) {
                    showStatus('Add at least one keyword first so there is an x button to use.', false, true);
                    return;
                }

                const wasExpanded = isIntentCategoryExpanded(target.category);
                setExpandedIntentCategories(prev => ({ ...prev, [target.category]: true }));
                window.setTimeout(() => {
                    highlightIntentDetectionControl('delete-button', target.element, true);
                }, wasExpanded ? 120 : 220);
                break;
            }
            default:
                break;
        }
    };

    const clearDraftResponseToneGuideHighlight = () => {
        if (draftToneGuideHighlightTimeoutRef.current) {
            window.clearTimeout(draftToneGuideHighlightTimeoutRef.current);
            draftToneGuideHighlightTimeoutRef.current = null;
        }

        setHighlightedDraftResponseToneControl('');
    };

    const highlightDraftResponseToneControl = (controlKey, element, focusTarget = null) => {
        if (!element) {
            return;
        }

        clearDraftResponseToneGuideHighlight();
        setHighlightedDraftResponseToneControl(controlKey);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const resolvedFocusTarget = focusTarget || (typeof element.focus === 'function' ? element : null);
        if (resolvedFocusTarget && typeof resolvedFocusTarget.focus === 'function') {
            resolvedFocusTarget.focus();
        }

        draftToneGuideHighlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightedDraftResponseToneControl('');
            draftToneGuideHighlightTimeoutRef.current = null;
        }, 2400);
    };

    const handleDraftResponseToneGuideAction = (controlKey) => {
        setIsDraftResponseToneExpanded(true);

        switch (controlKey) {
            case 'polite':
                window.setTimeout(() => {
                    const focusTarget = declineToneGroupRef.current?.querySelector('input');
                    highlightDraftResponseToneControl('polite', declineToneGroupRef.current, focusTarget);
                }, 120);
                break;
            case 'forwarding':
                window.setTimeout(() => {
                    const focusTarget = forwardToneGroupRef.current?.querySelector('input');
                    highlightDraftResponseToneControl('forwarding', forwardToneGroupRef.current, focusTarget);
                }, 120);
                break;
            case 'tone-scale':
                window.setTimeout(() => {
                    highlightDraftResponseToneControl('tone-scale', declineToneScaleRef.current);
                }, 120);
                break;
            default:
                break;
        }
    };

    const clearAiFeaturesGuideHighlight = () => {
        if (aiFeaturesGuideHighlightTimeoutRef.current) {
            window.clearTimeout(aiFeaturesGuideHighlightTimeoutRef.current);
            aiFeaturesGuideHighlightTimeoutRef.current = null;
        }

        setHighlightedAiFeaturesControl('');
    };

    const highlightAiFeaturesControl = (controlKey, element) => {
        if (!element) {
            return;
        }

        clearAiFeaturesGuideHighlight();
        setHighlightedAiFeaturesControl(controlKey);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const focusTarget = element.querySelector('input');
        if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus();
        }

        aiFeaturesGuideHighlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightedAiFeaturesControl('');
            aiFeaturesGuideHighlightTimeoutRef.current = null;
        }, 2400);
    };

    const handleAiFeaturesGuideAction = (controlKey) => {
        switch (controlKey) {
            case 'classification':
                window.setTimeout(() => {
                    highlightAiFeaturesControl('classification', aiClassificationItemRef.current);
                }, 120);
                break;
            case 'reply-generation':
                window.setTimeout(() => {
                    highlightAiFeaturesControl('reply-generation', aiReplyGenerationItemRef.current);
                }, 120);
                break;
            default:
                break;
        }
    };
    // Start editing a keyword
    const startEditingKeyword = (folderId, index, currentValue, lang = 'english', category = null) => {
        setEditingKeyword({ folderId, index, value: currentValue, lang, category });
    };

    // Save edited keyword
    const saveEditingKeyword = () => {
        if (editingKeyword.category) {
            // Editing intent detection keyword
            const { category, index, value } = editingKeyword;
            switch (category) {
                case 'decline':
                    const newDeclineKeywords = [...declineKeywords];
                    newDeclineKeywords[index] = value;
                    setDeclineKeywords(newDeclineKeywords);
                    break;
                case 'forward':
                    const newForwardKeywords = [...forwardKeywords];
                    newForwardKeywords[index] = value;
                    setForwardKeywords(newForwardKeywords);
                    break;
                case 'exclude':
                    const newExcludeKeywords = [...excludeKeywords];
                    newExcludeKeywords[index] = value;
                    setExcludeKeywords(newExcludeKeywords);
                    break;
            }
        } else if (editingKeyword.folderId !== null && editingKeyword.index !== null && editingKeyword.lang) {
            // Update the keyword in the correct language array
            setDynamicFolders(prev => {
                const newFolders = { ...prev };
                const folder = newFolders[editingKeyword.folderId];
                if (folder && folder.keywords && folder.keywords[editingKeyword.lang]) {
                    folder.keywords[editingKeyword.lang][editingKeyword.index] = editingKeyword.value;
                }
                return newFolders;
            });
        }
        setEditingKeyword({ folderId: null, index: null, value: '', lang: null, category: null });
    };

    // Cancel editing
    const cancelEditingKeyword = () => {
        setEditingKeyword({ folderId: null, index: null, value: '', lang: null, category: null });
    };

    // Handle edit input change
    const handleEditChange = (value) => {
        setEditingKeyword(prev => ({ ...prev, value }));
    };

    // Handle key press in edit mode
    const handleEditKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveEditingKeyword();
        } else if (e.key === 'Escape') {
            cancelEditingKeyword();
        }
    };

    // Show delete confirmation
    const confirmDeleteKeyword = (folderId, index, lang = 'english', category = null, keywordValue = null) => {
        if (category) {
            // Intent detection keyword
            let keyword = '';
            switch (category) {
                case 'decline':
                    keyword = declineKeywords[index] || '';
                    break;
                case 'forward':
                    keyword = forwardKeywords[index] || '';
                    break;
                case 'exclude':
                    keyword = excludeKeywords[index] || '';
                    break;
            }
            setKeywordToDelete({ folderId: null, index, keyword, lang: null, category });
        } else {
            // Folder keyword
            const folder = dynamicFolders[folderId];
            const keyword = folder?.keywords?.[lang]?.[index] || '';
            setKeywordToDelete({ folderId, index, keyword, lang, category: null });
        }
    };

    // Execute delete after confirmation
    const executeDeleteKeyword = () => {
        if (keywordToDelete) {
            if (keywordToDelete.category) {
                // Delete intent detection keyword
                const { category, index } = keywordToDelete;
                switch (category) {
                    case 'decline':
                        const newDeclineKeywords = [...declineKeywords];
                        newDeclineKeywords.splice(index, 1);
                        setDeclineKeywords(newDeclineKeywords);
                        break;
                    case 'forward':
                        const newForwardKeywords = [...forwardKeywords];
                        newForwardKeywords.splice(index, 1);
                        setForwardKeywords(newForwardKeywords);
                        break;
                    case 'exclude':
                        const newExcludeKeywords = [...excludeKeywords];
                        newExcludeKeywords.splice(index, 1);
                        setExcludeKeywords(newExcludeKeywords);
                        break;
                }
            } else {
                // Delete folder keyword
                setDynamicFolders(prev => {
                    const newFolders = { ...prev };
                    const folder = newFolders[keywordToDelete.folderId];
                    if (folder && folder.keywords && folder.keywords[keywordToDelete.lang]) {
                        folder.keywords[keywordToDelete.lang].splice(keywordToDelete.index, 1);
                    }
                    return newFolders;
                });
            }
        }
        setKeywordToDelete(null);
    };

    // Cancel delete
    const cancelDeleteKeyword = () => {
        setKeywordToDelete(null);
    };

    // Handle new keywords input change
    const handleNewKeywordsInputChange = (key, value) => {
        setNewKeywordsInput(prev => ({ ...prev, [key]: value }));
    };

    // Add multiple keywords from input
    const addMultipleKeywords = (key) => {
        const input = newKeywordsInput[key] || '';
        const keywords = input
            .split(',')
            .map(k => k.trim())
            .filter(k => k.length > 0 && k.length <= 100); // Max 100 chars per keyword

        if (keywords.length === 0) {
            showStatus('Please enter at least one keyword', false);
            return;
        }

        // Check if this is an intent detection category
        if (['decline', 'forward', 'exclude'].includes(key)) {
            switch (key) {
                case 'decline':
                    setDeclineKeywords(prev => [...prev, ...keywords]);
                    break;
                case 'forward':
                    setForwardKeywords(prev => [...prev, ...keywords]);
                    break;
                case 'exclude':
                    setExcludeKeywords(prev => [...prev, ...keywords]);
                    break;
            }
            // Clear input
            setNewKeywordsInput(prev => ({ ...prev, [key]: '' }));
            showStatus(`Added ${keywords.length} keyword(s)`, true);
            return;
        }

        // Ensure folder has keywords structure and add to first available language or create 'english'
        setDynamicFolders(prev => {
            const newFolders = {...prev};
            if (!newFolders[key]) {
                newFolders[key] = { id: key, name: 'New Folder', keywords: { english: [] } };
            }
            if (!newFolders[key].keywords) {
                newFolders[key].keywords = { english: [] };
            }

            // Find first available language array or default to 'english'
            const keywordsObj = newFolders[key].keywords;
            let targetLang = 'english';

            // If english doesn't exist but other languages do, use the first available
            if (!keywordsObj.english) {
                const availableLangs = Object.keys(keywordsObj).filter(k => Array.isArray(keywordsObj[k]));
                if (availableLangs.length > 0) {
                    targetLang = availableLangs[0];
                } else {
                    keywordsObj.english = [];
                }
            }

            // Add all keywords to the target language array
            keywords.forEach(keyword => {
                if (!keywordsObj[targetLang]) {
                    keywordsObj[targetLang] = [];
                }
                keywordsObj[targetLang].push(keyword);
            });

            return newFolders;
        });

        // Clear input
        setNewKeywordsInput(prev => ({ ...prev, [key]: '' }));
        showStatus(`Added ${keywords.length} keyword(s)`, true);
    };

    // Handle key press in add input
    const handleAddKeyPress = (e, key) => {
        if (e.key === 'Enter') {
            addMultipleKeywords(key);
        }
    };

    // Truncate keyword for display
    const truncateKeyword = (keyword, maxLength = 25) => {
        if (!keyword || typeof keyword !== 'string') return '';
        if (keyword.length <= maxLength) return keyword;
        return keyword.substring(0, maxLength - 3) + '...';
    };

    // Add a new keyword field for other categories
    const addKeywordField = (category) => {
        switch (category) {
            case 'decline':
                setDeclineKeywords([...declineKeywords, '']);
                break;
            case 'forward':
                setForwardKeywords([...forwardKeywords, '']);
                break;
            case 'exclude':
                setExcludeKeywords([...excludeKeywords, '']);
                break;
        }
    };

    // Remove a keyword field for other categories
    const removeKeywordField = (category, index) => {
        switch (category) {
            case 'decline':
                const newDeclineKeywords = [...declineKeywords];
                newDeclineKeywords.splice(index, 1);
                setDeclineKeywords(newDeclineKeywords);
                break;
            case 'forward':
                const newForwardKeywords = [...forwardKeywords];
                newForwardKeywords.splice(index, 1);
                setForwardKeywords(newForwardKeywords);
                break;
            case 'exclude':
                const newExcludeKeywords = [...excludeKeywords];
                newExcludeKeywords.splice(index, 1);
                setExcludeKeywords(newExcludeKeywords);
                break;
        }
    };

    // Handle keyword change for other categories
    const handleKeywordChange = (category, index, value) => {
        switch (category) {
            case 'decline':
                const newDeclineKeywords = [...declineKeywords];
                newDeclineKeywords[index] = value;
                setDeclineKeywords(newDeclineKeywords);
                break;
            case 'forward':
                const newForwardKeywords = [...forwardKeywords];
                newForwardKeywords[index] = value;
                setForwardKeywords(newForwardKeywords);
                break;
            case 'exclude':
                const newExcludeKeywords = [...excludeKeywords];
                newExcludeKeywords[index] = value;
                setExcludeKeywords(newExcludeKeywords);
                break;
        }
    };

    // Show status message with click-to-hide and auto-hide after 4 seconds
    const showStatus = (message, isSuccess = true, isNeutral = false) => {
        setStatusBar({
            show: true,
            message: message,
            isSuccess: isSuccess
        });
        setErrorType(isNeutral ? 'neutral' : (isSuccess ? 'success' : 'error'));
    };

    // Auto-hide status bar after 4 seconds
    useEffect(() => {
        if (statusBar.show) {
            const timer = setTimeout(() => {
                setStatusBar(prev => ({ ...prev, show: false }));
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [statusBar.show]);

    // Collect all parameters from the form
    const collectParameters = () => {
        // Convert ID-based folders back to name-as-key format for API
        const apiFormatFolders = {};
        Object.values(dynamicFolders).forEach(folder => {
            if (folder.name) {
                apiFormatFolders[folder.name] = {
                    description: folder.description || '',
                    keywords: folder.keywords || { english: [] }
                };
            }
        });

        return {
            userEmail: userEmails.filter(email => email.trim() !== '').join(','),
            dynamicFolders: apiFormatFolders,
            keywords: {
                decline: declineKeywords.filter(keyword => keyword.trim() !== ''),
                forward: forwardKeywords.filter(keyword => keyword.trim() !== '')
            },
            exclusions: {
                list: excludeKeywords.filter(keyword => keyword.trim() !== '')
            },
            tones: {
                decline: declineTone,
                forward: forwardTone
            },
            aiFeatures: {
                classification: aiClassification,
                replyGeneration: aiReplyGeneration,
                replyDetection: aiReplyDetection
            }
        };
    };

    // Validate parameters
    const validateParameters = (params) => {
        // Reset conflicting fields
        setConflictingFields([]);

        // Check that at least one email is provided and all emails are valid
        const emails = params.userEmail.split(',').map(email => email.trim()).filter(email => email !== '');
        if (emails.length === 0) {
            return { valid: false, message: "Please enter at least one email address", conflictingFields: [] };
        }
        for (const email of emails) {
            if (!email.includes('@')) {
                return { valid: false, message: "Please enter valid email addresses", conflictingFields: [] };
            }
        }

        // Check that each folder has a name (only if folders exist)
        for (const folderName in params.dynamicFolders) {
            if (!folderName.trim()) {
                return { valid: false, message: "All folders must have a name", conflictingFields: [] };
            }
        }

        // Check for conflicts between keywords in different sections
        const allKeywords = [];
        const keywordConflicts = [];
        const conflictingFieldsList = [];

        // Collect all keywords with their categories
        const keywordSections = [];

        // Add dynamic folder keywords
        for (const folderName in params.dynamicFolders) {
            const folder = params.dynamicFolders[folderName];
            if (folder.keywords && folder.keywords.english) {
                keywordSections.push({
                    name: `folder-${folderName}`,
                    keywords: folder.keywords.english
                });
            }
        }

        // Add other keywords
        keywordSections.push({ name: 'decline', keywords: params.keywords?.decline });
        keywordSections.push({ name: 'forward', keywords: params.keywords?.forward });
        keywordSections.push({ name: 'exclude', keywords: params.exclusions?.list });

        // Check for duplicates within and across categories
        keywordSections.forEach(section => {
            if (Array.isArray(section.keywords)) {
                section.keywords.forEach((keyword, index) => {
                    if (keyword.trim() !== '') {
                        const existing = allKeywords.find(k => k.keyword.toLowerCase() === keyword.toLowerCase());
                        if (existing) {
                            keywordConflicts.push({
                                keyword: keyword,
                                sections: [existing.section, section.name]
                            });
                            // Add both fields to conflicting fields list
                            conflictingFieldsList.push(`${existing.section}-${existing.index}`);
                            conflictingFieldsList.push(`${section.name}-${index}`);
                        } else {
                            allKeywords.push({
                                keyword: keyword,
                                section: section.name,
                                index: index
                            });
                        }
                    }
                });
            }
        });

        // Report conflicts if found
        if (keywordConflicts.length > 0) {
            const conflictMessages = keywordConflicts.map(conflict =>
                `"${conflict.keyword}" appears in both ${conflict.sections.join(' and ')} sections`
            );
            return {
                valid: false,
                message: `Keyword conflicts detected: ${conflictMessages.join('; ')}. Please resolve these conflicts before saving.`,
                conflictingFields: conflictingFieldsList
            };
        }

        return { valid: true, message: null, conflictingFields: [] };
    };

    // Save parameters to context and send to external API
    const saveParameters = async () => {
        const params = collectParameters();
        const validationResult = validateParameters(params);

        // Set conflicting fields for visual feedback
        setConflictingFields(validationResult.conflictingFields);

        if (!validationResult.valid) {
            showStatus(validationResult.message, false);
            return;
        }

        setError(null);
        startLoading('Saving, please wait ...');

        try {
            updateParameters(params);

            const targetEmail = selectedEmail || configuredEmails[0] || (user && user.email) || '';

            if (targetEmail) {
                const apiParametersData = {
                    userEmail: targetEmail,
                    dynamicFolders: params.dynamicFolders,
                    keywords: params.keywords,
                    exclusions: params.exclusions,
                    tones: params.tones,
                    aiFeatures: params.aiFeatures
                };

                const headers = {
                    'Content-Type': 'application/json',
                };

                if (user && user.tokenType && user.accessToken) {
                    headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
                }

                const response = await fetch(`/server/rest/ai-agent/parameters/${targetEmail}`, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify(apiParametersData),
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    showStatus(`Parameters saved successfully for ${targetEmail}. The mail agent will use these settings on its next run.`);
                } else {
                    showStatus(`Parameters saved locally but failed to send to external API for ${targetEmail}: ` + (data.message || 'Unknown error'), false);
                }
            } else {
                showStatus('Parameters saved locally, but no target email was available to sync.', false);
            }
        } catch (e) {
            showStatus('Error saving parameters: ' + e.message, false);
        } finally {
            stopLoading();
        }
    };

    // Reset to default parameters
    const resetToDefaults = () => {
        if (window.confirm("Are you sure you want to reset all parameters to their default values?")) {
            // Reset dynamic folders
            setDynamicFolders(defaultParams.dynamicFolders);

            // Reset other keyword arrays
            setDeclineKeywords([...defaultParams.keywords.decline]);
            setForwardKeywords([...defaultParams.keywords.forward]);
            setExcludeKeywords([...defaultParams.exclusions.list]);

            setUserEmails([]);
            setSelectedEmail('');
            setDeclineTone(defaultParams.tones.decline);
            setForwardTone(defaultParams.tones.forward);

            setAiClassification(defaultParams.aiFeatures.classification);
            setAiReplyGeneration(defaultParams.aiFeatures.replyGeneration);
            setAiReplyDetection(defaultParams.aiFeatures.replyDetection);

            showStatus("Parameters reset to default values. Don't forget to save if you want to keep these settings.");
        }
    };

    return (
        <div className="email-automation-container">
            <div className="header-container">
                <h1>Email Automation Dashboard</h1>
                <div className="nav-links">
                    <a href="/#/ai/automations/email" className={`nav-link ${location.pathname === '/ai/automations/email' ? 'active' : ''}`}>Email Dashboard</a>
                    <a href="/#/ai/automations/email/schedule" className={`nav-link ${location.pathname === '/ai/automations/email/schedule' ? 'active' : ''}`}>Schedule Configuration</a>
                    <a href="/#/ai/automations/email/params" className={`nav-link ${location.pathname === '/ai/automations/email/params' ? 'active' : ''}`}>Email Parameters</a>
                </div>
            </div>
            <div className="container">

                {loading && (
                    <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
                        <div className="loading-panel">
                            <div className="loading-icon-wrap" aria-hidden="true">
                                <span className="loading-icon-ring loading-icon-ring-outer"></span>
                                <span className="loading-icon-ring loading-icon-ring-inner"></span>
                                <span className="loading-icon-core">
                                    <SyncRoundedIcon className="loading-icon-symbol" />
                                </span>
                            </div>
                            <p className="loading-text">{loadingMessage}</p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="status-bar error">
                        <p>Error: {error}</p>
                    </div>
                )}

                {statusBar.show && (
                    <div
                        className={`status-bar ${errorType}`}
                        onClick={() => setStatusBar(prev => ({ ...prev, show: false }))}
                    >
                        <p>{statusBar.message}</p>
                    </div>
                )}

                <form>
                    <div className="section">
                        <h2 className="section-title">User Settings</h2>
                        <div className="form-group">
                            <label htmlFor={hasConfiguredEmails ? "configured-email-select" : undefined}>User Emails:</label>
                            {needsEmailSetup && (
                                <div id="email-setup-instructions" className="email-setup-notice" role="status">
                                    Enter the email address you want to automate, then press Enter to add it.
                                </div>
                            )}

                            {hasConfiguredEmails && (
                                <div className="email-configured-row">
                                    <select
                                        id="configured-email-select"
                                        className="configured-emails-select"
                                        value={selectedEmail || configuredEmails[0]}
                                        onChange={handleSelectedEmailChange}
                                    >
                                        {configuredEmails.map(email => (
                                            <option key={email} value={email}>{email}</option>
                                        ))}
                                    </select>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={confirmDeleteSelectedEmail}
                                        className="remove-email-btn"
                                    >
                                        Delete Email
                                    </Button>
                                </div>
                            )}

                            <div className="add-keywords-row email-input-row">
                                <input
                                    ref={emailInputRef}
                                    type="email"
                                    className={`add-keywords-input ${needsEmailSetup ? 'email-setup-highlight' : ''}`}
                                    value={newEmailInput}
                                    onChange={(e) => handleNewEmailInputChange(e.target.value)}
                                    onKeyDown={handleNewEmailKeyPress}
                                    placeholder="Enter email address..."
                                    aria-describedby={needsEmailSetup ? 'email-setup-instructions' : undefined}
                                />
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={addNewEmail}
                                    className="add-keywords-btn"
                                    disabled={!newEmailInput.trim()}
                                >
                                    + Add Email
                                </Button>
                            </div>
                        </div>
                    </div>

                    {hasConfiguredEmails && (
                    <div className="section">
                        <h2
                            className="section-title section-title-toggle"
                            onClick={() => setIsDynamicFoldersExpanded(!isDynamicFoldersExpanded)}
                            title={isDynamicFoldersExpanded ? "Click to collapse" : "Click to expand"}
                        >
                            <span className="section-title-main">
                                <span className="toggle-arrow">{isDynamicFoldersExpanded ? '\u25BC' : '\u25B6'}</span>
                                <span>Dynamic Folders</span>
                            </span>
                            <button
                                type="button"
                                className={`section-help-btn ${isDynamicFoldersHelpOpen ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsDynamicFoldersHelpOpen(prev => !prev);
                                }}
                                aria-label={isDynamicFoldersHelpOpen ? "Hide Dynamic Folders help" : "Show Dynamic Folders help"}
                                aria-expanded={isDynamicFoldersHelpOpen}
                                title={isDynamicFoldersHelpOpen ? "Hide Dynamic Folders help" : "Show Dynamic Folders help"}
                            >
                                ?
                            </button>
                        </h2>
                        {isDynamicFoldersHelpOpen && (
                            <div className="dynamic-folders-help">
                                <p>Dynamic Folders let you create and manage folders the email automation can use when organizing messages.</p>
                                <ol className="help-guide-list">
                                    <li><strong>How to add a folder:</strong> Click <button type="button" className="help-action-link" onClick={() => handleDynamicFoldersGuideAction('add-folder')}>+ Add Folder</button> to create a new Dynamic Folder card.</li>
                                    <li><strong>How to delete a folder:</strong> Click <button type="button" className="help-action-link" onClick={() => handleDynamicFoldersGuideAction('remove-folder')}>Remove Folder</button> to highlight the first available remove action.</li>
                                    <li><strong>How to update the name of the folder:</strong> Click <button type="button" className="help-action-link" onClick={() => handleDynamicFoldersGuideAction('folder-name')}>Folder Name</button> to jump to the first folder name input.</li>
                                    <li><strong>How to update the description:</strong> Click <button type="button" className="help-action-link" onClick={() => handleDynamicFoldersGuideAction('description')}>Description</button> to expand the first folder when needed and highlight its description field.</li>
                                </ol>
                                <div className="help-guide-note">
                                    <strong>Important:</strong> Click <strong>Save Parameters</strong> before leaving this page so you do not lose your changes.
                                </div>
                            </div>
                        )}
                        {isDynamicFoldersExpanded && (
                        <>
                            <div className="form-group">
                                <Button
                                    ref={addFolderButtonRef}
                                    variant="contained"
                                    color="success"
                                    onClick={addFolder}
                                    className={`add-folder-btn ${highlightedDynamicFoldersControl === 'add-folder' ? 'guide-highlight-target' : ''}`}
                                >
                                    + Add Folder
                                </Button>
                            </div>

                            {Object.entries(dynamicFolders).map(([folderId, folder]) => (
                            <div key={folderId} className="folder-section">
                                <div className="folder-header">
                                    <button
                                        type="button"
                                        className="folder-toggle-btn"
                                        onClick={() => toggleFolderExpand(folderId)}
                                        title={isFolderExpanded(folderId) ? "Collapse folder" : "Expand folder"}
                                    >
                                        {isFolderExpanded(folderId) ? '\u25BC' : '\u25B6'}
                                    </button>
                                    <input
                                        ref={getFirstDynamicFolderId() === folderId ? firstFolderNameInputRef : null}
                                        type="text"
                                        className={`folder-name-input ${getFirstDynamicFolderId() === folderId && highlightedDynamicFoldersControl === 'folder-name' ? 'guide-highlight-target' : ''}`}
                                        value={folder.name}
                                        onChange={(e) => updateFolderName(folderId, e.target.value)}
                                        placeholder="Folder Name"
                                    />
                                    <Button
                                        ref={getFirstDynamicFolderId() === folderId ? firstRemoveFolderButtonRef : null}
                                        variant="contained"
                                        color="error"
                                        onClick={() => removeFolder(folderId)}
                                        className={`remove-folder-btn ${getFirstDynamicFolderId() === folderId && highlightedDynamicFoldersControl === 'remove-folder' ? 'guide-highlight-target' : ''}`}
                                    >
                                        Remove Folder
                                    </Button>
                                </div>

                                {isFolderExpanded(folderId) && (
                                    <div className="form-group folder-keywords-group">
                                        <div className="folder-description-group">
                                            <label>Description:</label>
                                            <textarea
                                                ref={getFirstDynamicFolderId() === folderId ? firstDescriptionTextareaRef : null}
                                                className={`folder-description-input ${getFirstDynamicFolderId() === folderId && highlightedDynamicFoldersControl === 'description' ? 'guide-highlight-target' : ''}`}
                                                value={folder.description || ''}
                                                onChange={(e) => updateFolderDescription(folderId, e.target.value)}
                                                placeholder="Enter folder description..."
                                                maxLength={500}
                                                rows={2}
                                            />
                                        </div>
                                        {/* Keywords hidden for now */}
                                        {hideFolderKeywords && (
                                            <div style={{ padding: '10px 0', color: '#666', fontStyle: 'italic' }}>
                                                Keywords are disabled for dynamic folders
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                        </>
                        )}
                    </div>
                    )}

                    {hasConfiguredEmails && (
                    <div className="section">
                        <h2
                            className="section-title section-title-toggle"
                            onClick={() => setIsIntentDetectionExpanded(!isIntentDetectionExpanded)}
                            title={isIntentDetectionExpanded ? "Click to collapse" : "Click to expand"}
                        >
                            <span className="section-title-main">
                                <span className="toggle-arrow">{isIntentDetectionExpanded ? '\u25BC' : '\u25B6'}</span>
                                <span>Intent Detection</span>
                            </span>
                            <button
                                type="button"
                                className={"section-help-btn " + (isIntentDetectionHelpOpen ? "active" : "")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsIntentDetectionHelpOpen(prev => !prev);
                                }}
                                aria-label={isIntentDetectionHelpOpen ? "Hide Intent Detection help" : "Show Intent Detection help"}
                                aria-expanded={isIntentDetectionHelpOpen}
                                title={isIntentDetectionHelpOpen ? "Hide Intent Detection help" : "Show Intent Detection help"}
                            >
                                ?
                            </button>
                        </h2>
                        {isIntentDetectionHelpOpen && (
                            <div className="dynamic-folders-help intent-detection-help">
                                <p>Intent Detection keywords help the email automation understand the type of message it received and what action should happen next.</p>
                                <ol className="help-guide-list">
                                    <li><button type="button" className="help-action-link" onClick={() => handleIntentDetectionGuideAction('decline-label')}>Polite Decline Keywords</button>: Use this group for messages the system should politely decline, such as spam, solicitations, or requests you do not want to accept.</li>
                                    <li><button type="button" className="help-action-link" onClick={() => handleIntentDetectionGuideAction('forward-label')}>Forwarding Action Keywords</button>: Use this group for messages that should be sent along or routed to someone else for follow-up.</li>
                                    <li><button type="button" className="help-action-link" onClick={() => handleIntentDetectionGuideAction('exclude-label')}>Exclude/Ignore Keywords</button>: Use this group for messages the automation should skip and leave untouched.</li>
                                    <li><strong>Add keywords:</strong> Type one or more comma-separated keywords in <button type="button" className="help-action-link" onClick={() => handleIntentDetectionGuideAction('input')}>INPUT</button>, then click <button type="button" className="help-action-link" onClick={() => handleIntentDetectionGuideAction('add-button')}>+ Add</button>.</li>
                                    <li><strong>Eliminate keywords:</strong> Click <button type="button" className="help-action-link" onClick={() => handleIntentDetectionGuideAction('delete-button')}>x</button> on a keyword chip to remove it.</li>
                                </ol>
                                <div className="help-guide-note">
                                    <strong>Important:</strong> Always click <strong>Save Parameters</strong> after making changes, or your updates may be lost.
                                </div>
                            </div>
                        )}
                        {isIntentDetectionExpanded && (
                        <>
                            {/* Polite Decline Keywords */}
                        <div className="form-group folder-section">
                            <div className="folder-header">
                                <button
                                    type="button"
                                    className="folder-toggle-btn"
                                    onClick={() => toggleIntentCategory('decline')}
                                    title={isIntentCategoryExpanded('decline') ? "Collapse" : "Expand"}
                                >
                                    {isIntentCategoryExpanded('decline') ? '\u25BC' : '\u25B6'}
                                </button>
                                <label
                                    ref={declineLabelRef}
                                    className={"intent-keywords-label " + (highlightedIntentDetectionControl === "decline-label" ? "guide-highlight-target guide-highlight-label" : "")}
                                >
                                    Polite Decline Keywords:
                                </label>
                            </div>
                            {isIntentCategoryExpanded('decline') && (
                            <div className="form-group folder-keywords-group">
                                <div className="keywords-chips-container">
                                    {declineKeywords.map((keyword, index) => {
                                        const isEditing = editingKeyword.category === 'decline' && editingKeyword.index === index;
                                        const isConflicting = conflictingFields.includes(`decline-${index}`);

                                        return (
                                            <div key={index} className="keyword-chip-wrapper">
                                                {isEditing ? (
                                                    <div className="keyword-chip-editing">
                                                        <input
                                                            type="text"
                                                            value={editingKeyword.value}
                                                            onChange={(e) => handleEditChange(e.target.value)}
                                                            onKeyDown={handleEditKeyPress}
                                                            onBlur={saveEditingKeyword}
                                                            autoFocus
                                                            maxLength={100}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="chip-action-btn save"
                                                            onClick={saveEditingKeyword}
                                                            title="Save"
                                                        >
                                                            {'\u2713'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="chip-action-btn cancel"
                                                            onClick={cancelEditingKeyword}
                                                            title="Cancel"
                                                        >
                                                            {'\u2715'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={`keyword-chip ${isConflicting ? 'conflicting-chip' : ''}`}
                                                        onClick={() => startEditingKeyword(null, index, keyword, null, 'decline')}
                                                        title={`Click to edit: ${keyword}`}
                                                    >
                                                        <span className="keyword-text">{truncateKeyword(keyword)}</span>
                                                        <button
                                                            ref={index === 0 ? declineDeleteButtonRef : null}
                                                            type="button"
                                                            className={"chip-delete-btn " + (highlightedIntentDetectionControl === "delete-button" && declineKeywords.length > 0 ? "guide-highlight-target" : "")}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                confirmDeleteKeyword(null, index, null, 'decline');
                                                            }}
                                                            title="Delete keyword"
                                                        >
                                                            {'\u00D7'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="add-keywords-row">
                                    <input
                                        ref={declineInputRef}
                                        type="text"
                                        className={"add-keywords-input " + (highlightedIntentDetectionControl === "input" ? "guide-highlight-target" : "")}
                                        value={newKeywordsInput['decline'] || ''}
                                        onChange={(e) => handleNewKeywordsInputChange('decline', e.target.value)}
                                        onKeyDown={(e) => handleAddKeyPress(e, 'decline')}
                                        placeholder="Enter keywords, comma separated..."
                                        maxLength={500}
                                    />
                                    <Button
                                        ref={declineAddButtonRef}
                                        variant="contained"
                                        color="success"
                                        onClick={() => addMultipleKeywords('decline')}
                                        className={"add-keywords-btn " + (highlightedIntentDetectionControl === "add-button" ? "guide-highlight-target" : "")}
                                        disabled={!(newKeywordsInput['decline'] || '').trim()}
                                    >
                                        + Add
                                    </Button>
                                </div>
                            </div>
                            )}
                        </div>

                        {/* Forwarding Action Keywords */}
                        <div className="form-group folder-section">
                            <div className="folder-header">
                                <button
                                    type="button"
                                    className="folder-toggle-btn"
                                    onClick={() => toggleIntentCategory('forward')}
                                    title={isIntentCategoryExpanded('forward') ? "Collapse" : "Expand"}
                                >
                                    {isIntentCategoryExpanded('forward') ? '\u25BC' : '\u25B6'}
                                </button>
                                <label
                                    ref={forwardLabelRef}
                                    className={"intent-keywords-label " + (highlightedIntentDetectionControl === "forward-label" ? "guide-highlight-target guide-highlight-label" : "")}
                                >
                                    Forwarding Action Keywords:
                                </label>
                            </div>
                            {isIntentCategoryExpanded('forward') && (
                            <div className="form-group folder-keywords-group">
                                <div className="keywords-chips-container">
                                    {forwardKeywords.map((keyword, index) => {
                                        const isEditing = editingKeyword.category === 'forward' && editingKeyword.index === index;
                                        const isConflicting = conflictingFields.includes(`forward-${index}`);

                                        return (
                                            <div key={index} className="keyword-chip-wrapper">
                                                {isEditing ? (
                                                    <div className="keyword-chip-editing">
                                                        <input
                                                            type="text"
                                                            value={editingKeyword.value}
                                                            onChange={(e) => handleEditChange(e.target.value)}
                                                            onKeyDown={handleEditKeyPress}
                                                            onBlur={saveEditingKeyword}
                                                            autoFocus
                                                            maxLength={100}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="chip-action-btn save"
                                                            onClick={saveEditingKeyword}
                                                            title="Save"
                                                        >
                                                            {'\u2713'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="chip-action-btn cancel"
                                                            onClick={cancelEditingKeyword}
                                                            title="Cancel"
                                                        >
                                                            {'\u2715'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={`keyword-chip ${isConflicting ? 'conflicting-chip' : ''}`}
                                                        onClick={() => startEditingKeyword(null, index, keyword, null, 'forward')}
                                                        title={`Click to edit: ${keyword}`}
                                                    >
                                                        <span className="keyword-text">{truncateKeyword(keyword)}</span>
                                                        <button
                                                            ref={index === 0 ? forwardDeleteButtonRef : null}
                                                            type="button"
                                                            className={"chip-delete-btn " + (highlightedIntentDetectionControl === "delete-button" && declineKeywords.length === 0 && forwardKeywords.length > 0 ? "guide-highlight-target" : "")}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                confirmDeleteKeyword(null, index, null, 'forward');
                                                            }}
                                                            title="Delete keyword"
                                                        >
                                                            {'\u00D7'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="add-keywords-row">
                                    <input
                                        type="text"
                                        className="add-keywords-input"
                                        value={newKeywordsInput['forward'] || ''}
                                        onChange={(e) => handleNewKeywordsInputChange('forward', e.target.value)}
                                        onKeyDown={(e) => handleAddKeyPress(e, 'forward')}
                                        placeholder="Enter keywords, comma separated..."
                                        maxLength={500}
                                    />
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => addMultipleKeywords('forward')}
                                        className="add-keywords-btn"
                                        disabled={!(newKeywordsInput['forward'] || '').trim()}
                                    >
                                        + Add
                                    </Button>
                                </div>
                            </div>
                            )}
                        </div>

                        {/* Exclude/Ignore Keywords */}
                        <div className="form-group folder-section">
                            <div className="folder-header">
                                <button
                                    type="button"
                                    className="folder-toggle-btn"
                                    onClick={() => toggleIntentCategory('exclude')}
                                    title={isIntentCategoryExpanded('exclude') ? "Collapse" : "Expand"}
                                >
                                    {isIntentCategoryExpanded('exclude') ? '\u25BC' : '\u25B6'}
                                </button>
                                <label
                                    ref={excludeLabelRef}
                                    className={"intent-keywords-label " + (highlightedIntentDetectionControl === "exclude-label" ? "guide-highlight-target guide-highlight-label" : "")}
                                >
                                    Exclude/Ignore Keywords:
                                </label>
                            </div>
                            {isIntentCategoryExpanded('exclude') && (
                            <div className="form-group folder-keywords-group">
                                <div className="keywords-chips-container">
                                    {excludeKeywords.map((keyword, index) => {
                                        const isEditing = editingKeyword.category === 'exclude' && editingKeyword.index === index;
                                        const isConflicting = conflictingFields.includes(`exclude-${index}`);

                                        return (
                                            <div key={index} className="keyword-chip-wrapper">
                                                {isEditing ? (
                                                    <div className="keyword-chip-editing">
                                                        <input
                                                            type="text"
                                                            value={editingKeyword.value}
                                                            onChange={(e) => handleEditChange(e.target.value)}
                                                            onKeyDown={handleEditKeyPress}
                                                            onBlur={saveEditingKeyword}
                                                            autoFocus
                                                            maxLength={100}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="chip-action-btn save"
                                                            onClick={saveEditingKeyword}
                                                            title="Save"
                                                        >
                                                            {'\u2713'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="chip-action-btn cancel"
                                                            onClick={cancelEditingKeyword}
                                                            title="Cancel"
                                                        >
                                                            {'\u2715'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={`keyword-chip ${isConflicting ? 'conflicting-chip' : ''}`}
                                                        onClick={() => startEditingKeyword(null, index, keyword, null, 'exclude')}
                                                        title={`Click to edit: ${keyword}`}
                                                    >
                                                        <span className="keyword-text">{truncateKeyword(keyword)}</span>
                                                        <button
                                                            ref={index === 0 ? excludeDeleteButtonRef : null}
                                                            type="button"
                                                            className={"chip-delete-btn " + (highlightedIntentDetectionControl === "delete-button" && declineKeywords.length === 0 && forwardKeywords.length === 0 && excludeKeywords.length > 0 ? "guide-highlight-target" : "")}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                confirmDeleteKeyword(null, index, null, 'exclude');
                                                            }}
                                                            title="Delete keyword"
                                                        >
                                                            {'\u00D7'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="add-keywords-row">
                                    <input
                                        type="text"
                                        className="add-keywords-input"
                                        value={newKeywordsInput['exclude'] || ''}
                                        onChange={(e) => handleNewKeywordsInputChange('exclude', e.target.value)}
                                        onKeyDown={(e) => handleAddKeyPress(e, 'exclude')}
                                        placeholder="Enter keywords, comma separated..."
                                        maxLength={500}
                                    />
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => addMultipleKeywords('exclude')}
                                        className="add-keywords-btn"
                                        disabled={!(newKeywordsInput['exclude'] || '').trim()}
                                    >
                                        + Add
                                    </Button>
                                </div>
                            </div>
                            )}
                        </div>
                    </>
                    )}
                    </div>
                    )}

                    {hasConfiguredEmails && (
                    <div className="section">
                        <h2
                            className="section-title section-title-toggle"
                            onClick={() => setIsDraftResponseToneExpanded(!isDraftResponseToneExpanded)}
                            title={isDraftResponseToneExpanded ? "Click to collapse" : "Click to expand"}
                        >
                            <span className="section-title-main">
                                <span className="toggle-arrow">{isDraftResponseToneExpanded ? '\u25BC' : '\u25B6'}</span>
                                <span>Draft Response Tone</span>
                            </span>
                            <button
                                type="button"
                                className={"section-help-btn " + (isDraftResponseToneHelpOpen ? "active" : "")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsDraftResponseToneHelpOpen(prev => !prev);
                                }}
                                aria-label={isDraftResponseToneHelpOpen ? "Hide Draft Response Tone help" : "Show Draft Response Tone help"}
                                aria-expanded={isDraftResponseToneHelpOpen}
                                title={isDraftResponseToneHelpOpen ? "Hide Draft Response Tone help" : "Show Draft Response Tone help"}
                            >
                                ?
                            </button>
                        </h2>
                        {isDraftResponseToneHelpOpen && (
                            <div className="dynamic-folders-help draft-response-tone-help">
                                <p>Draft Response Tone controls how the automation writes reply drafts for the main intent types it handles.</p>
                                <ol className="help-guide-list">
                                    <li><button type="button" className="help-action-link" onClick={() => handleDraftResponseToneGuideAction('polite')}>Polite Decline Tone</button>: Adjusts how reserved or warm the draft should sound when the automation politely says no.</li>
                                    <li><button type="button" className="help-action-link" onClick={() => handleDraftResponseToneGuideAction('forwarding')}>Forwarding Action Tone</button>: Adjusts the wording for drafts that acknowledge the message and explain that it is being forwarded or routed for follow-up.</li>
                                    <li><strong>Formal-Neutral-Friendly:</strong> Click <button type="button" className="help-action-link" onClick={() => handleDraftResponseToneGuideAction('tone-scale')}>the tone scale</button> to see where the slider lands. Formal is more professional and reserved, Neutral is balanced, and Friendly is warmer and more conversational.</li>
                                </ol>
                                <div className="help-guide-note">
                                    <strong>Important:</strong> Always click <strong>Save Parameters</strong> after making changes, or your updates may be lost.
                                </div>
                            </div>
                        )}
                        {isDraftResponseToneExpanded && (
                        <>
                        <div
                            ref={declineToneGroupRef}
                            className={"form-group " + (highlightedDraftResponseToneControl === "polite" ? "guide-highlight-target" : "")}
                        >
                            <label htmlFor="decline-tone">Polite Decline Tone:</label>
                            <input
                                type="range"
                                id="decline-tone"
                                min="1"
                                max="5"
                                value={declineTone}
                                onChange={(e) => setDeclineTone(parseInt(e.target.value, 10))}
                            />
                            <div
                                ref={declineToneScaleRef}
                                className={"tone-labels " + (highlightedDraftResponseToneControl === "tone-scale" ? "guide-highlight-target tone-scale-highlight" : "")}
                            >
                                <span>Formal</span>
                                <span>Neutral</span>
                                <span>Friendly</span>
                            </div>
                        </div>
                        <div
                            ref={forwardToneGroupRef}
                            className={"form-group " + (highlightedDraftResponseToneControl === "forwarding" ? "guide-highlight-target" : "")}
                        >
                            <label htmlFor="forward-tone">Forwarding Action Tone:</label>
                            <input
                                type="range"
                                id="forward-tone"
                                min="1"
                                max="5"
                                value={forwardTone}
                                onChange={(e) => setForwardTone(parseInt(e.target.value, 10))}
                            />
                            <div className={"tone-labels " + (highlightedDraftResponseToneControl === "tone-scale" ? "guide-highlight-target tone-scale-highlight" : "")}>
                                <span>Formal</span>
                                <span>Neutral</span>
                                <span>Friendly</span>
                            </div>
                        </div>
                        </>
                        )}
                    </div>
                    )}

                    {hasConfiguredEmails && (
                    <div className="section">
                        <h2 className="section-title section-title-with-help">
                            <span>AI Features</span>
                            <button
                                type="button"
                                className={"section-help-btn " + (isAiFeaturesHelpOpen ? "active" : "")}
                                onClick={() => setIsAiFeaturesHelpOpen(prev => !prev)}
                                aria-label={isAiFeaturesHelpOpen ? "Hide AI Features help" : "Show AI Features help"}
                                aria-expanded={isAiFeaturesHelpOpen}
                                title={isAiFeaturesHelpOpen ? "Hide AI Features help" : "Show AI Features help"}
                            >
                                ?
                            </button>
                        </h2>
                        {isAiFeaturesHelpOpen && (
                            <div className="dynamic-folders-help ai-features-help">
                                <p>AI Features control which AI-powered steps are enabled while the automation processes and responds to email.</p>
                                <ol className="help-guide-list">
                                    <li><button type="button" className="help-action-link" onClick={() => handleAiFeaturesGuideAction('classification')}>AI Classification</button>: Uses AI to understand the incoming message so the automation can sort or route it more accurately.</li>
                                    <li><button type="button" className="help-action-link" onClick={() => handleAiFeaturesGuideAction('reply-generation')}>AI Reply Generation</button>: Uses AI to draft responses based on the intent rules and tone settings you configured above.</li>
                                </ol>
                                <div className="help-guide-note">
                                    <strong>Important:</strong> Always click <strong>Save Parameters</strong> after making changes, or your updates may be lost.
                                </div>
                            </div>
                        )}
                        <div className="checkbox-group">
                            <div
                                ref={aiClassificationItemRef}
                                className={"checkbox-item " + (highlightedAiFeaturesControl === "classification" ? "guide-highlight-target" : "")}
                            >
                                <input
                                    type="checkbox"
                                    id="ai-classification"
                                    checked={aiClassification}
                                    onChange={(e) => setAiClassification(e.target.checked)}
                                />
                                <label htmlFor="ai-classification">AI Classification</label>
                            </div>
                            <div
                                ref={aiReplyGenerationItemRef}
                                className={"checkbox-item " + (highlightedAiFeaturesControl === "reply-generation" ? "guide-highlight-target" : "")}
                            >
                                <input
                                    type="checkbox"
                                    id="ai-reply-generation"
                                    checked={aiReplyGeneration}
                                    onChange={(e) => setAiReplyGeneration(e.target.checked)}
                                />
                                <label htmlFor="ai-reply-generation">AI Reply Generation</label>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasConfiguredEmails && (
                    <div className="actions">
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={saveParameters}
                            className="btn btn-secondary"
                            disabled={loading}
                        >
                            Save Parameters
                        </Button>
                    </div>
                    )}
                </form>

                {folderToDelete && (
                    <div className="delete-confirmation-overlay">
                        <div className="delete-confirmation-dialog">
                            <h3>Remove Folder</h3>
                            <p>Are you sure you want to remove the folder "<strong>{folderToDelete.name}</strong>"?</p>
                            <div className="delete-confirmation-actions">
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={executeRemoveFolder}
                                    className="btn-delete-confirm"
                                >
                                    Remove
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={cancelRemoveFolder}
                                    className="btn-delete-cancel"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Dialog for Keywords */}
                {keywordToDelete && (
                    <div className="delete-confirmation-overlay">
                        <div className="delete-confirmation-dialog">
                            <h3>Delete Keyword</h3>
                            <p>Are you sure you want to delete the keyword "<strong>{keywordToDelete.keyword}</strong>"?</p>
                            <div className="delete-confirmation-actions">
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={executeDeleteKeyword}
                                    className="btn-delete-confirm"
                                >
                                    Delete
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={cancelDeleteKeyword}
                                    className="btn-delete-cancel"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Dialog for Emails */}
                {emailToDelete && (
                    <div className="delete-confirmation-overlay">
                        <div className="delete-confirmation-dialog">
                            <h3>Delete Email</h3>
                            <p>Are you sure you want to delete the email "<strong>{emailToDelete.email}</strong>"?</p>
                            <div className="delete-confirmation-actions">
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={executeDeleteEmail}
                                    className="btn-delete-confirm"
                                >
                                    Delete
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={cancelDeleteEmail}
                                    className="btn-delete-cancel"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default EmailParameters;
